import { Edge } from './types';
import { Graph } from './graph';

export function networkSimplexRankAssignment(
  nodes: string[],
  edges: Edge[],
  preds: Map<string, string[]>,
  succs: Map<string, string[]>,
  ranksep: number
): Map<string, number> {
  // Build graph compatible with dagre algorithms
  const g = new Graph(true);
  for (const node of nodes) {
    g.setNode(node, { rank: 0 });
  }
  for (const e of edges) {
    const weight = parseFloat(String(e.attributes.weight || '1'));
    const minlen = 1; // one rank separation
    g.setEdge(e.from, e.to, { weight, minlen });
  }

  // Run network simplex
  try {
    networkSimplex(g);
  } catch (error) {
    // Fall back to longest path
    longestPath(g);
  }

  // Extract ranks
  const ranks = new Map<string, number>();
  for (const v of g.nodes()) {
    const rank = g.node(v).rank! * ranksep;
    ranks.set(v, rank);
  }
  // Ensure all ranks are finite numbers
  for (const [node, rank] of ranks) {
    if (isNaN(rank) || !isFinite(rank)) {
      console.warn(`Fixing invalid rank for ${node}: ${rank} -> 0`);
      ranks.set(node, 0);
    }
  }
  return ranks;
}

// Following functions adapted from dagre/lib/rank/network-simplex.js

function networkSimplex(g: Graph): void {
  // simplify not needed
  longestPath(g);
  const t = feasibleTree(g);
  initLowLimValues(t);
  initCutValues(t, g);

  let e, f;
  let iteration = 0;
  while ((e = leaveEdge(t))) {
    f = enterEdge(t, g, e);
    exchangeEdges(t, g, e, f);
    iteration++;
    if (iteration > 100) {
      break;
    }
  }
}

function longestPath(g: Graph): void {
  const visited = new Set<string>();
  const dfs = (v: string): number => {
    const label = g.node(v);
    if (visited.has(v)) {
      return label.rank!;
    }
    visited.add(v);

    let minRank = Infinity;
    for (const e of g.outEdges(v)) {
      const edge = g.edge(e.v, e.w)!;
      const wRank = dfs(e.w);
      minRank = Math.min(minRank, wRank - edge.minlen);
    }

    if (minRank === Infinity) {
      minRank = 0;
    }
    label.rank = minRank;
    return minRank;
  };

  // Find sources (nodes with no incoming edges)
  const sources = g.nodes().filter((v) => g.inEdges(v).length === 0);
  sources.forEach(dfs);
}

function slack(g: Graph, e: { v: string; w: string }): number {
  return g.node(e.w).rank! - g.node(e.v).rank! - g.edge(e.v, e.w)!.minlen;
}

function feasibleTree(g: Graph): Graph {
  const t = new Graph(false); // undirected tree

  const start = g.nodes()[0];
  t.setNode(start, {});
  const size = 0;

  function tightTree(): number {
    const visited = new Set<string>();
    const dfs = (v: string) => {
      for (const e of g.nodeEdges(v)) {
        const edgeV = e.v;
        const w = v === edgeV ? e.w : edgeV;
        if (!t.hasNode(w) && slack(g, e) === 0) {
          t.setNode(w, {});
          t.setEdge(v, w, {});
          dfs(w);
        }
      }
    };
    t.nodes().forEach((v) => dfs(v));
    return t.nodeCount();
  }

  while (tightTree() < g.nodeCount()) {
    let minSlackEdge: { v: string; w: string } | null = null;
    let minSlack = Infinity;
    for (const e of g.edges()) {
      if (t.hasNode(e.v) !== t.hasNode(e.w)) {
        const s = slack(g, e);
        if (s < minSlack) {
          minSlack = s;
          minSlackEdge = e;
        }
      }
    }
    if (!minSlackEdge) break;

    const delta = t.hasNode(minSlackEdge.v) ? minSlack : -minSlack;
    // Shift ranks of nodes in tree
    for (const v of t.nodes()) {
      g.node(v).rank! += delta;
    }
  }

  return t;
}

function initLowLimValues(tree: Graph, root?: string): void {
  if (!root) {
    const nodes = tree.nodes();
    if (nodes.length === 0) {
      console.warn('initLowLimValues: tree has no nodes');
      return;
    }
    root = nodes[0];
  }
  let nextLim = 1;
  const visited = new Set<string>();

  function dfsAssignLowLim(v: string, parent?: string): number {
    const low = nextLim;
    const label = tree.node(v);
    visited.add(v);

    for (const w of tree.neighbors(v)) {
      if (!visited.has(w)) {
        nextLim = dfsAssignLowLim(w, v);
      }
    }

    label.low = low;
    label.lim = nextLim++;
    if (parent) {
      label.parent = parent;
    } else {
      delete label.parent;
    }
    return nextLim;
  }

  dfsAssignLowLim(root);
}

function initCutValues(t: Graph, g: Graph): void {
  // postorder traversal
  const visited = new Set<string>();
  const order: string[] = [];

  function postorder(v: string) {
    visited.add(v);
    for (const w of t.neighbors(v)) {
      if (!visited.has(w)) {
        postorder(w);
      }
    }
    order.push(v);
  }
  for (const v of t.nodes()) {
    if (!visited.has(v)) {
      postorder(v);
    }
  }
  // Remove root (last element)
  if (order.length > 0) {
    order.pop();
  }
  for (const v of order) {
    assignCutValue(t, g, v);
  }
}

function assignCutValue(t: Graph, g: Graph, child: string): void {
  const childLab = t.node(child);
  const {parent} = childLab;
  if (!parent) {
    console.warn('assignCutValue: child', child, 'has no parent');
    return;
  }
  const edge = t.edge(child, parent)!;
  edge.cutvalue = calcCutValue(t, g, child);
}

function calcCutValue(t: Graph, g: Graph, child: string): number {
  const childLab = t.node(child);
  const parent = childLab.parent!;
  let childIsTail = true;
  let graphEdge = g.edge(child, parent);
  if (!graphEdge) {
    childIsTail = false;
    graphEdge = g.edge(parent, child)!;
  }

  let cutValue = graphEdge.weight;

  for (const e of g.nodeEdges(child)) {
    const isOutEdge = e.v === child;
    const other = isOutEdge ? e.w : e.v;
    if (other !== parent) {
      const pointsToHead = isOutEdge === childIsTail;
      const otherWeight = g.edge(e.v, e.w)!.weight;
      cutValue += pointsToHead ? otherWeight : -otherWeight;
      if (isTreeEdge(t, child, other)) {
        const otherCutValue = t.edge(child, other)!.cutvalue!;
        cutValue += pointsToHead ? -otherCutValue : otherCutValue;
      }
    }
  }

  return cutValue;
}

function isTreeEdge(tree: Graph, u: string, v: string): boolean {
  return tree.hasEdge(u, v);
}

function leaveEdge(tree: Graph): { v: string; w: string } | null {
  for (const e of tree.edges()) {
    const edge = tree.edge(e.v, e.w);
    if (edge?.cutvalue !== undefined && edge.cutvalue < 0) {
      return e;
    }
  }
  return null;
}

function enterEdge(t: Graph, g: Graph, edge: { v: string; w: string }): { v: string; w: string } {
  let {v} = edge;
  let {w} = edge;
  if (!g.hasEdge(v, w)) {
    v = edge.w;
    w = edge.v;
  }

  const vLabel = t.node(v);
  const wLabel = t.node(w);
  let tailLabel = vLabel;
  let flip = false;
  if (vLabel.lim! > wLabel.lim!) {
    tailLabel = wLabel;
    flip = true;
  }

  const candidates = g.edges().filter((e) => {
    const eVLabel = t.node(e.v);
    const eWLabel = t.node(e.w);
    return (
      flip === isDescendant(t, eVLabel, tailLabel) && flip !== isDescendant(t, eWLabel, tailLabel)
    );
  });

  if (candidates.length === 0) {
    console.warn('enterEdge: no candidates found, returning first edge');
    return g.edges()[0] || edge;
  }

  return candidates.reduce((acc, e) => {
    if (slack(g, e) < slack(g, acc)) {
      return e;
    }
    return acc;
  });
}

function isDescendant(t: Graph, vLabel: any, rootLabel: any): boolean {
  return rootLabel.low <= vLabel.lim && vLabel.lim <= rootLabel.lim;
}

function exchangeEdges(
  t: Graph,
  g: Graph,
  e: { v: string; w: string },
  f: { v: string; w: string }
): void {
  t.removeEdge(e.v, e.w);
  t.setEdge(f.v, f.w, {});
  initLowLimValues(t);
  initCutValues(t, g);
  updateRanks(t, g);
}

function updateRanks(t: Graph, g: Graph): void {
  const root = t.nodes().find((v) => !g.node(v).parent);
  if (!root) {
    console.warn('updateRanks: no root found');
    return;
  }
  const visited = new Set<string>();
  const stack = [root];
  const order: string[] = [];
  while (stack.length) {
    const v = stack.pop()!;
    if (visited.has(v)) continue;
    visited.add(v);
    order.push(v);
    for (const w of t.neighbors(v)) {
      if (!visited.has(w)) {
        stack.push(w);
      }
    }
  }
  // skip root (first)
  for (let i = 1; i < order.length; i++) {
    const v = order[i];
    const parent = t.node(v).parent!;
    let edge = g.edge(v, parent);
    let flipped = false;
    if (!edge) {
      edge = g.edge(parent, v)!;
      flipped = true;
    }
    g.node(v).rank = g.node(parent).rank! + (flipped ? edge.minlen : -edge.minlen);
  }
}
